/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from "styled-components";
import {MRT_ColumnDef, MRT_GlobalFilterTextField, MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton, MRT_ToggleGlobalFilterButton} from "material-react-table";
import ArgosDialog from "../../components/Dialogs/ArgosDialog";
import {Box, Checkbox, FormControl, FormControlLabel, FormHelperText, IconButton, Tooltip} from "@mui/material";
import {Add as AddIcon, Edit as EditIcon} from "@mui/icons-material";
import {tableButtonGray} from "../../../constants/colors";
import {usePostRequest} from "../../../api/helper";
import ArgosFlexbox from "../../components/Containers/ArgosFlexbox";
import ArgosMenuItem from '../../components/Form/Select/ArgosMenuItem';
import ArgosSelect from "../../components/Form/Select/ArgosSelect";
import ArgosTextField from "../../components/Form/TextField/ArgosTextField";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks";
import {selectAccessControlState, selectCurrentUser} from "../../../redux/ducks/AccessControl/slice";
import ArgosButton, {ArgosLoadingButton} from "../../components/Buttons/ArgosButton";
import toast from "react-hot-toast";
import {getIsUserAdmin, useHasWriteAccess, useGetCurrentPageUserAccessLevel} from "../../../redux/ducks/AccessControl/accessControlHelpers";
import {setGlobalDialogComponent, setIsGlobalDialogOpen} from "../../../redux/ducks/GlobalDialog/slice";
import {setGlobalRightClickMenuData, setGlobalRightClickMenuPosition} from "../../../redux/ducks/GlobalRightClickMenu/slice";
import PageviewOutlined from "@mui/icons-material/PageviewOutlined";
import ADAS_PROGRAMS_CONSTANTS from "./constants";
import DeleteIcon from "@mui/icons-material/Delete";
import {stringToDateToLocaleString} from "../../../helpers/helpers";
import BoxWithCenteredContent from "../../components/Containers/BoxWithCenteredContent";
import {ELoadingStateTypes, selectLoadingState, setLoadingState1} from "../../../redux/ducks/Misc/slice";
import Loader from "../../components/Loader/Loader";
import {ArgosTable} from '../../components/Table/ArgosTable';
import useGetHubName from "../../components/Socket/useGetHubName";
import useSocket from "../../components/Socket/useSocket";
import SelectableList from "../../components/Lists/SelectableList";
import VAR_COMMON_CONSTANTS from "../../../constants/globalVariables"
import {VEHICLE_PROGRAMS_CONSTANTS} from "../vehiclePrograms/constants";
import {allowAdminOrOwnerPermissions, deleteDisablePermissions, determineSaveState, planOfRecordDisablePermissions} from '../../../helpers/permissionHelpers';

function generateModelYearRange(isString: boolean = false) {
  let modelYears = []
  for (let i = 2023; i < 2036; i++) {
    modelYears.push(isString ? i.toString() : i)
  }
  return modelYears
}

const MODEL_YEAR_SELECT_DATA = generateModelYearRange(true)

export const Container = styled.div({
  display: 'flex',
  flexFlow: 'column',
  overflow: 'auto',
  height: '100%',
})
const sortFu = (rowA: any, rowB: any, columnId: any) => {
  if (rowA.getParentRow()?.getIsExpanded() && rowB.getParentRow()?.getIsExpanded())
    return rowA.getValue(columnId) < rowB.getValue(columnId) ? 1 : -1
  else
    return 0
}
const COLUMNS = [
  {
    id: 'programName',
    header: 'ADAS Program',
    size: 300,
  },
  {
    id: 'modelYear',
    header: 'Model Year',
  },
  {
    id: 'description',
    header: 'Description',
    size: 220,
  },
  {
    id: 'createdBy',
    header: 'Created By',
  },
  {
    id: 'isPlanOfRecord',
    header: 'Plan of Record',
    Cell: ({cell}: any) =>  !cell.row.original.subRows?.length && <FormControlLabel
    control={<Checkbox
      style={{justifyContent: "center"}}
      checked={cell.row.original.isPlanOfRecord}
      disabled={true}
    />} label=""
    style={{justifyContent: "center"}}></FormControlLabel>,    
    sortingFn: (rowA: any, rowB: any, columnId: any): number => sortFu(rowA, rowB, columnId)
  },
  {
    id: 'isProgramArchive',
    header: 'Archive',
    Cell: ({cell}: any) =>  !cell.row.original.subRows?.length && <FormControlLabel
    control={<Checkbox
      style={{justifyContent: "center"}}
      checked={cell.row.original.isProgramArchive}
      disabled={true}
    />} label=""
    style={{justifyContent: "center"}}></FormControlLabel>,
    sortingFn: (rowA: any, rowB: any, columnId: any): number => sortFu(rowA, rowB, columnId),
  },
  {
    id: 'dateCreated',
    header: 'Date Created',
  },
  {
    id: 'lastUpdated',
    header: 'Last Updated',
  },
  // {
  //   id: 'runTime',
  //   header: 'Run Time',
  // },
]

interface Row {
  name: string
}

const tableToolbarActions = (table: any, hasWriteAccess: boolean, setIsAddProgramDialogOpen: any, extraProps: any) => {
  const isForceUpdateReportsLoading = extraProps.isForceUpdateReportsLoading ?? false
  const updateOperationStatus = extraProps.updateState?.status
  const isForceUpdateReportsInProgress = updateOperationStatus === 'In Progress' || isForceUpdateReportsLoading
  const isAdmin = extraProps.isAdmin
  return (
    <Box>
      {isAdmin &&
        <ArgosLoadingButton
          style={{backgroundColor: isForceUpdateReportsInProgress ? 'lightblue' : 'initial'}}
          onClick={() => extraProps.setIsForceUpdateReportsDialogOpen(true)}
        >
          Force Update Reports
        </ArgosLoadingButton>
      }
      {hasWriteAccess && <Tooltip title="Add Adas Program" arrow placement="bottom">
        <IconButton onClick={() => setIsAddProgramDialogOpen(true)} size="medium">
          <AddIcon style={{color: tableButtonGray}}/>
        </IconButton>
      </Tooltip>}
      <MRT_ToggleGlobalFilterButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ToggleFiltersButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ToggleFullScreenButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ShowHideColumnsButton table={table} style={{color: tableButtonGray}}/>
    </Box>
  )
}

const DEFAULT_FAILED_DELETE_PROGRAM_ERROR_MESSAGE = "Error deleting ADAS program."
const AdasPrograms = () => {
  const dispatch = useAppDispatch()
  const fetchHandle = usePostRequest('/HttpTriggerAdasPrograms?page=landingTable')
  const [isAddProgramDialogOpen, setIsAddProgramDialogOpen] = React.useState(false)
  const [isEditProgramDialogOpen, setIsEditProgramDialogOpen] = React.useState(false)
  const [currentEditProgram, setCurrentEditProgram] = React.useState<any>(null)
  /**
   * Access control
   */
  const [isUserAdmin, userAccessLevel] = useGetCurrentPageUserAccessLevel()
  const hasWriteAccess = useHasWriteAccess()
  const gmid = useAppSelector(state => selectCurrentUser(state).id)
  const columns = useMemo(() => COLUMNS.map((column) => {
    const col: MRT_ColumnDef<Row> = {
      ...column,
      accessorFn: (row: any) => {
        const isParent = row.subRows?.length
        if (isParent) {
          const hide = column.id !== 'programName'
          return hide ? '' : row[column.id] ?? ''
        } else {
          const hide = column.id === 'programName'
          let value = row[column.id]
          if (column.id === 'dateCreated' || column.id === 'lastUpdated') {
            value = stringToDateToLocaleString(row[column.id])
          } /*Keeping this in case if errors with moving the following code to Cell
          else if (column.id === "isPlanOfRecord") {
            value = <FormControlLabel
              control={<Checkbox
                style={{justifyContent: "center"}}
                checked={row.isPlanOfRecord}
                disabled={true}
              />} label=""
              style={{justifyContent: "center"}}></FormControlLabel>
          }
          else if (column.id === "isProgramArchive") {
            value = <FormControlLabel
              control={<Checkbox
                style={{justifyContent: "center"}}
                checked={row.isProgramArchive}
                disabled={true}
              />} label=""
              style={{justifyContent: "center"}}></FormControlLabel>
          }*/
          return hide ? '' : value ?? ''
        }
      },        
      filterVariant: column.id === 'isProgramArchive' || 'isPlanOfRecord' ? 'checkbox' : undefined,
    }
    return col
  }), [])
  const [data, setData] = React.useState<Row[]>([])
  const [programsList, setProgramsList] = React.useState<any[]>([])
  const [programNametoModelYearsMap, setProgramNametoModelYearsMap] = React.useState(null)
  const [isTableLoading, setIsTableLoading] = useState(true);

  function setTableState(res: any[]) {
    setData(res)
    // get list of programs names and codes as flat list
    const programNames = res.map((program: any) => {
      return {
        programName: program.programName,
      }
    })
    setProgramsList(programNames)

    // create map of programName to set of modelYears with that programName
    let _programNameToModelYears: any = {}
    for (let i = 0; i < res.length; i++) {
      // loop through subRows and add to map
      for (let j = 0; j < res[i].subRows.length; j++) {
        const {programName, modelYear} = res[i].subRows[j]
        if (!_programNameToModelYears[programName]) {
          _programNameToModelYears[programName] = new Set()
        }
        _programNameToModelYears[programName].add(modelYear)
      }
    }
    setProgramNametoModelYearsMap(_programNameToModelYears)
  }

  const fetchData = () => {
    setIsTableLoading(true)
    fetchHandle()
      .then((res) => {
        setIsTableLoading(false)
        setTableState(res);
      })
      .catch((err) => {
        setIsTableLoading(false)
        console.error(err)
      })
  }
  React.useEffect(() => {
    fetchData()
  }, [])

  const handleEditSelect = (e: any) => {
    setIsEditProgramDialogOpen(true)
    const programName = e.getParentRow().original.programName
    setCurrentEditProgram({...e.original, programName})
  }

  const deleteProgramHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.landingPage.deleteProgram)
  const isDeleteProgramLoadingState = useAppSelector((state) => selectLoadingState(state, ELoadingStateTypes.DELETE_ADAS_PROGRAM))
  const isDeleteProgramLoading = isDeleteProgramLoadingState?.isLoading ?? false
  const isDeleteProgramLoadingData = isDeleteProgramLoadingState?.data ?? null

  const deleteProgram = (adasProgramId: string) => {
    const payload = {
      adasProgramId
    }
    dispatch(setLoadingState1(ELoadingStateTypes.DELETE_ADAS_PROGRAM, true, {id: adasProgramId}))
    deleteProgramHandle(payload)
      .then((res) => {
        setTableState(res)
        setDeleteAdasProgramDialogData(null)
      })
      .catch((err) => {
        setDeleteAdasProgramDialogData({
          deleteInvalid: true,
          message: DEFAULT_FAILED_DELETE_PROGRAM_ERROR_MESSAGE,
          data: {id: adasProgramId}
        })
      }).finally(() => {
      dispatch(setLoadingState1(ELoadingStateTypes.DELETE_ADAS_PROGRAM, false, {id: adasProgramId}))
    })
  }

  const [deleteAdasProgramDialogData, setDeleteAdasProgramDialogData] = React.useState<any>(null)
  const onClickDeleteProgram = (_data: any) => {
    setDeleteAdasProgramDialogData({initialLoad: true})
    checkIsDeleteValid(_data)
  }

  const checkIsDeleteValidHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.landingPage.checkIsDeleteProgramValid)
  const checkIsDeleteValid = (_data: any) => {
    dispatch(setLoadingState1(ELoadingStateTypes.DELETE_ADAS_PROGRAM, true, {id: _data?.id}))
    let _deleteDialogData: any = {
      data: _data
    }
    checkIsDeleteValidHandle({adasProgramId: _data?.id}).then((res) => {
      dispatch(setLoadingState1(ELoadingStateTypes.DELETE_ADAS_PROGRAM, false, {id: _data?.id}))
      if (res?.shouldDelete) {
        _deleteDialogData.message = res?.message
      } else {
        const errorMsg = res?.message ?? "Failed to check if program can be deleted."
        _deleteDialogData.deleteInvalid = true
        _deleteDialogData.message = errorMsg
      }
      setDeleteAdasProgramDialogData(_deleteDialogData)
    }).catch((err) => {
      _deleteDialogData.deleteInvalid = true
      _deleteDialogData.message = "Failed to check if program can be deleted."
      setDeleteAdasProgramDialogData(_deleteDialogData)
    }).finally(() => {
      dispatch(setLoadingState1(ELoadingStateTypes.DELETE_ADAS_PROGRAM, false, {id: _data?.id}))
    })
  }

  /**
   * Force update reports state
   */
  const [updateState, setUpdateState] = useState<any>()

  // Fetch initial state
  const fetchStateHandle = usePostRequest('/HttpTriggerAdasPrograms?page=landingTable&requestType=fetchForceUpdateReportsOperationState')
  useEffect(() => {
    fetchStateHandle()
      .then((res) => {
        setUpdateState((prevState: any) => ({...prevState, ...res}))
      })
      .catch((err) => {
        console.error(err)
      })
  }, [])

  const user = useAppSelector(selectCurrentUser)
  const isAdmin = getIsUserAdmin(user)

  // Get forceUpdateAdasReports messages from socket
  const onMessage = useCallback((msg: any) => {
    setUpdateState((prevState: any) => ({...prevState, ...msg}))
  }, [])
  const hubName = useGetHubName(`forceUpdateAdasReports`)
  useSocket(hubName, onMessage, isAdmin)

  const [isForceUpdateReportsDialogOpen, setIsForceUpdateReportsDialogOpen] = React.useState(false)
  const [isForceUpdateReportsLoading, setIsForceUpdateReportsLoading] = React.useState(false)

  const updateOperationStatus = updateState?.status

  const toolbarProps = {
    isForceUpdateReportsDialogOpen,
    setIsForceUpdateReportsDialogOpen,
    isForceUpdateReportsLoading,
    setIsForceUpdateReportsLoading,
    updateState,
    isAdmin
  }
  const initialState = {
    columnFilters: [
      { id: 'isProgramArchive', value: 'false' }, // Set initial filter for 'isProgramArchive' column
    ],
  }
  const deleteDialogMessage = deleteAdasProgramDialogData?.message
  const initialDeleteDialogLoading = isDeleteProgramLoading && deleteAdasProgramDialogData?.initialLoad
  useEffect(() => {
    document.title = ADAS_PROGRAMS_CONSTANTS.sidebar.title + ' - Landing';
    return () => {
      document.title = 'Argos';
    };
  }, []);

  /**
    * Function to determine the permissions for row actions based on the user's access level and ownership of the Adas program.
    * It checks if the user is an admin or has read-write access, and if they are the owner of the Adas program.
    * 
    * @param row - The row data for which the permissions are being checked.
    * @returns IRowActionPermissions - An object with boolean attributes set containing the permissions for editing and deleting the Adas program.
    */
  const rowActionsPermissions = (row: any): IRowActionPermissions => {
    const disabledFlag=deleteDisablePermissions(isUserAdmin, userAccessLevel, row.original.creatorGmid, gmid)
    return {
      editAdasProgram: hasWriteAccess,
      // Resusing disabled helper for checkbox control toggling (! not operator) to show control or not show control
      deleteAdasProgram: !disabledFlag,
    };
}


  return (
    <Container>
      <ArgosTable
        uid={'adas_1'}

        data={data}
        columns={columns}
        initialState={initialState}

        enableExpanding
        state={{
          isLoading: isTableLoading,
        }}

        muiExpandButtonProps={({row}) => ({
          // Show expand button only when row has sub-elements
          sx: {
            display: row.subRows?.length ? 'flex' : 'none'
          }
        })}

        enableRowActions
        displayColumnDefOptions={{
          'mrt-row-actions': {
            size: hasWriteAccess ? 230 : 120,
          },
        }}
        renderRowActions={({row}: any) => tableRowActions(row, rowActionsPermissions(row), () => {
          handleEditSelect(row)
        }, dispatch, onClickDeleteProgram)}
        renderToolbarInternalActions={({table}: any) => tableToolbarActions(table, hasWriteAccess, setIsAddProgramDialogOpen, toolbarProps)}
        enablePagination={false}
        enableBottomToolbar={false}
      />
      <ForceUpdateReportsDialog
        isForceUpdateReportsDialogOpen={isForceUpdateReportsDialogOpen}
        setIsForceUpdateReportsDialogOpen={(open) => {
          setIsForceUpdateReportsDialogOpen(open)
        }}
        isForceUpdateReportsLoading={isForceUpdateReportsLoading}
        setIsForceUpdateReportsLoading={setIsForceUpdateReportsLoading}
        updateState={updateState}
      />
      <ArgosDialog
        maxWidth={'fit-content'} minWidth={'0px'} height={'fit-content'}
        open={isAddProgramDialogOpen}
        onClose={() => {
          setIsAddProgramDialogOpen(false)
        }}
        headerTitle={'Add Adas Program'}
        headerTitleAlign={'left'}
      >
        <AddProgramDialog
          onCancel={(refresh: boolean = false) => {
            if (refresh) {
              fetchData()
            }
            setIsAddProgramDialogOpen(false)
          }}
          programsList={programsList}
          programNametoModelYearsMap={programNametoModelYearsMap}
        />
      </ArgosDialog>
      <ArgosDialog
        maxWidth={'fit-content'} minWidth={'0px'} height={'fit-content'}
        open={isEditProgramDialogOpen}
        onClose={() => {
          setIsEditProgramDialogOpen(false)
        }}
        headerTitle={'Edit Adas Program'}
        headerTitleAlign={'left'}
      >
        <EditProgramDialog
          onCancel={(refresh: boolean = false) => {
            if (refresh) {
              fetchData()
            }
            setIsEditProgramDialogOpen(false)
          }}
          row={currentEditProgram}
          programNametoModelYearsMap={programNametoModelYearsMap}
        />
      </ArgosDialog>

      <ArgosDialog open={deleteAdasProgramDialogData != null} onClose={() => setDeleteAdasProgramDialogData(null)}
                   width={'auto'} height={'auto'} headerTitle={'Delete ADAS Program'} headerTitleAlign={'left'}>
        <BoxWithCenteredContent horizontal vertical
                                style={{height: '100%', display: 'flex', flexFlow: 'column nowrap'}}>
          {deleteAdasProgramDialogData != null &&
            <Loader isLoading={initialDeleteDialogLoading}>
              <p style={{width: '300px', textAlign: 'center'}}>
                {String(deleteDialogMessage).split(",").map((p:any,index:any)=><p key={p}>{index ? '\u2022'+p : p}</p>)}
              </p>
            </Loader>
          }
          <ArgosFlexbox>
            <ArgosButton
              // disabled={isDeleteProgramLoading && !initialDeleteDialogLoading} // TODO: If do this then dialog reopens.
              disabled={isDeleteProgramLoading}
              onClick={() => setDeleteAdasProgramDialogData(null)}>CANCEL</ArgosButton>
            {
              !deleteAdasProgramDialogData?.deleteInvalid && !initialDeleteDialogLoading && deleteAdasProgramDialogData != null &&
              deleteAdasProgramDialogData?.data?.id &&
              <ArgosLoadingButton
                disabled={isDeleteProgramLoading}
                isLoading={isDeleteProgramLoading}
                onClick={() => {
                  deleteProgram(deleteAdasProgramDialogData?.data?.id)
                }}
              >
                DELETE
              </ArgosLoadingButton>
            }
            {/*{deleteAdasProgramDialogData?.deleteInvalid &&*/}
            {/*  <ArgosButton*/}
            {/*    disabled={isDeleteProgramLoading}*/}
            {/*    onClick={() => {*/}
            {/*      const url = ADAS_PROGRAMS_CONSTANTS.linkFunctions.itemDetailsMainHTabs(deleteAdasProgramDialogData?.data?.id,*/}
            {/*        deleteAdasProgramDialogData?.data?.name, deleteAdasProgramDialogData?.data?.modelYear,*/}
            {/*      'Vehicle Programs'*/}
            {/*    )*/}
            {/*      window.open(url, '_blank')*/}
            {/*    }}*/}
            {/*  >*/}
            {/*    GO*/}
            {/*  </ArgosButton>*/}
            {/*}*/}
          </ArgosFlexbox>
        </BoxWithCenteredContent>
      </ArgosDialog>
    </Container>
  )
}

const ForceUpdateReportsDialog = (
  props: {
    isForceUpdateReportsDialogOpen: boolean,
    setIsForceUpdateReportsDialogOpen: (open: boolean) => void,
    isForceUpdateReportsLoading: boolean,
    setIsForceUpdateReportsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    updateState: any
  }
) => {
  const {
    isForceUpdateReportsDialogOpen, setIsForceUpdateReportsDialogOpen,
    isForceUpdateReportsLoading, setIsForceUpdateReportsLoading, updateState
  } = props
  const saveHandle = usePostRequest('/HttpTriggerAdasPrograms?page=landingTable&requestType=forceUpdateReports')
  const onRun = () => {
    setIsForceUpdateReportsLoading(true)
    saveHandle({selectedAdasProgramIds})
      .then((res) => {
      })
      .catch((err) => {
        console.error(err)
        toast.error('Error saving program')
      }).finally(() => {
      setIsForceUpdateReportsLoading(false)
    })
  }

  const msgMap = useMemo(() => {
    if (updateState) {
      const {visualReportProgress} = updateState
      return (
        <pre>{JSON.stringify(visualReportProgress, null, 2)}</pre>
      )
    }
  }, [updateState])

  const packagesDataFetchHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchAdasPrograms)
  const [adasProgramIdToName, setAdasProgramIdToName] = useState<any>({})
  const [isFetchingAdasPrograms, setIsFetchingAdasPrograms] = useState(false)
  useEffect(() => {
    if (!isForceUpdateReportsDialogOpen) {
      return
    }
    setIsFetchingAdasPrograms(true)
    packagesDataFetchHandle().then((res) => {
      const _adasProgramIdToName: any = {}
      res.forEach((program: any) => {
        _adasProgramIdToName[program.adasProgramId] = ADAS_PROGRAMS_CONSTANTS.helperFunctions.getFullAdasProgramNameWithNameAndModelYear(program.data.programName, program.data.modelYear)
      })
      setAdasProgramIdToName(_adasProgramIdToName)
    }).catch((err) => {
      console.error(err)
    }).finally(() => {
      setIsFetchingAdasPrograms(false)
    })
  }, [isForceUpdateReportsDialogOpen]);
  const [selectedAdasProgramIds, setSelectedAdasProgramIds] = useState<string[]>(updateState?.selectedAdasProgramIds ?? [])
  useEffect(() => {
    setSelectedAdasProgramIds(updateState?.selectedAdasProgramIds ?? [])
  }, [updateState?.selectedAdasProgramIds]);

  useEffect(() => {
    if (isForceUpdateReportsDialogOpen)
      setSelectedAdasProgramIds(updateState?.selectedAdasProgramIds ?? [])
  }, [isForceUpdateReportsDialogOpen]);

  return (
    <ArgosDialog
      maxWidth={'fit-content'} minWidth={'0px'} height={'fit-content'}
      open={isForceUpdateReportsDialogOpen}
      onClose={() => {
        setIsForceUpdateReportsDialogOpen(false)
      }}
      headerTitle={'Force Update Reports'}
      headerTitleAlign={'left'}
    >
      <ArgosFlexbox vertical>
        <div>
          ENSURE YOU KNOW WHAT YOU ARE DOING. This will regenerate all reports for all programs.
        </div>
        <ArgosFlexbox>
          <FormControl sx={{width: 400}}>

            <Loader isLoading={isFetchingAdasPrograms}>
              <SelectableList
                items={Object.keys(adasProgramIdToName).map(id => ({id, name: adasProgramIdToName[id]}))}
                selectedItems={selectedAdasProgramIds}
                setSelectedItems={setSelectedAdasProgramIds}
                disabled={isForceUpdateReportsLoading}
              />
            </Loader>
          </FormControl>
        </ArgosFlexbox>
        <div>
          {msgMap}
        </div>
        <ArgosFlexbox style={{justifyContent: 'center'}}>
          <ArgosLoadingButton onClick={() => setIsForceUpdateReportsDialogOpen(false)} disabled={false}>Close</ArgosLoadingButton>
          <ArgosLoadingButton
            disabled={!Array.isArray(selectedAdasProgramIds) || selectedAdasProgramIds.length === 0}
            onClick={onRun}
            isLoading={isForceUpdateReportsLoading}
          >
            Run
          </ArgosLoadingButton>
        </ArgosFlexbox>
      </ArgosFlexbox>

    </ArgosDialog>
  )
}

const EditProgramDialog = (props: any) => {
  const [error, setError] = useState("");
  const [formData, setFormData] = React.useState<any>(props?.row);
  const saveHandle = usePostRequest('/HttpTriggerAdasPrograms?page=landingTable&requestType=editProgram');
  const [isSaving, setIsSaving] = React.useState(false);
  const [initialFormData, setInitialFormData] = useState<any>(props?.row);
  const owner: string = initialFormData?.creatorGmid
  const gmid = useAppSelector(state => selectCurrentUser(state).id)

  const [modelYears, setModelYears] = useState<string[]>(generateModelYearRange(true) as string[]);

  useEffect(() => {
    setFormData(props?.row);
    setInitialFormData(props?.row);
  }, [props?.row]);

  useEffect(() => {
    const allModelYears = generateModelYearRange(true) as string[];
    const excludedYears = new Set(props.programNametoModelYearsMap[formData?.programName]);
    const filteredModelYears = allModelYears.filter(year => {
      // Include the initial model year if the initial program is selected
      if (formData?.programName === initialFormData?.programName && year === initialFormData?.modelYear) {
        return true;
      }
      return !excludedYears.has(year);
    });
    setModelYears(filteredModelYears);
  }, [formData?.programName, initialFormData?.programName, initialFormData?.modelYear, props.programNametoModelYearsMap]);

  const onSave = () => {
    const payload = {
      ...formData,
      initialAdasProgramName: initialFormData?.programName,
    };
    setIsSaving(true);
    saveHandle(payload)
      .then((res) => {
        if (res.error) {
          toast.error(res.error, {duration: VEHICLE_PROGRAMS_CONSTANTS.toast.TOAST_ERROR_DURATION})
        } else {
          props.onCancel(true)
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error saving program');
      }).finally(() => {
      setIsSaving(false);
    });
  };
  const hasWriteAccess: boolean = useHasWriteAccess()
  const checkDisabled = () => {
    const wereRequiredUniqueValuesEdited = initialFormData?.programName != formData?.programName || initialFormData?.modelYear != formData?.modelYear;
    const duplicateModelYear = props?.programNametoModelYearsMap?.[formData?.programName]?.has(formData?.modelYear);
    if (wereRequiredUniqueValuesEdited && duplicateModelYear) {
      return true;
    }
    return false;
  };
  const isDuplicateModelYear = checkDisabled();

  const user = useAppSelector(selectCurrentUser);
  const isAdmin = getIsUserAdmin(user);

  const onChange = (e: any) => {
    const newValue = e.target.value;

    if (!newValue.match(VAR_COMMON_CONSTANTS.VAR_FORBIDDEN_CHARS)) {
      setError("");
    } else {
      setError(VAR_COMMON_CONSTANTS.VAR_FORBIDDEN_CHARS_MSG);
    }
    setFormData((prevState: any) => ({...prevState, programName: newValue}));
  };

  return (
    <ArgosFlexbox vertical>
      <FormControlLabel
        control={
          <Checkbox
            checked={formData?.isPlanOfRecord}
            onChange={(e) => setFormData((prevState: any) => ({...prevState, isPlanOfRecord: e.target.checked}))}
            disabled={initialFormData?.isProgramArchive || planOfRecordDisablePermissions(hasWriteAccess, owner, gmid)}
          />
        }
        label={'Plan Of Record'}/>
       <FormControlLabel
        control={
          <Checkbox
            checked={formData?.isProgramArchive}
            onChange={(e) => setFormData((prevState: any) => ({...prevState, isProgramArchive: e.target.checked}))}
            disabled={allowAdminOrOwnerPermissions(isAdmin, owner, gmid)}
          />
        }
        label={'Archive'}/>
      <ArgosTextField
        label={'Program Name'} required value={formData?.programName}
        onChange={onChange}
        error={!!error}
        helperText={error}
        disabled={initialFormData?.isProgramArchive}
      />

      <FormControl error={isDuplicateModelYear}>
        <ArgosSelect
          disabled={initialFormData?.isProgramArchive}
          error={isDuplicateModelYear}
          value={formData?.modelYear}
          label="Select Model Year"
          onChange={(e: any) => {
            setFormData((prevState: any) => ({...prevState, modelYear: e.target.value}));
          }}
        >
          {
            modelYears.map((modelYear, i) => {
              return (
                <ArgosMenuItem key={i} value={modelYear}>{modelYear}</ArgosMenuItem>
              );
            })
          }
        </ArgosSelect>
        <FormHelperText hidden={!isDuplicateModelYear}>
          {'duplicate model year'}
        </FormHelperText>
      </FormControl>

      <ArgosTextField
        rows={4}
        multiline label={"Notes/Description"} value={formData?.description}
        onChange={(e: any) => {
          setFormData((prevState: any) => ({...prevState, description: e.target.value}));
        }}
        disabled={initialFormData?.isProgramArchive}
      />

      <ArgosFlexbox style={{justifyContent: 'center'}}>
        <ArgosLoadingButton onClick={() => props?.onCancel()} disabled={isSaving}>Cancel</ArgosLoadingButton>
        <ArgosLoadingButton onClick={onSave} isLoading={isSaving} disabled={determineSaveState(formData?.isProgramArchive, initialFormData?.isProgramArchive)  || !!error || isDuplicateModelYear}>Save</ArgosLoadingButton>
      </ArgosFlexbox>
    </ArgosFlexbox>
  );
};

const AddProgramDialog = (props: any) => {
  const [error, setError] = useState("");
  const [checkedAddToExistingGroup, setCheckedAddToExistingGroup] = React.useState(false);
  const isPlanOfRecord: boolean = false
  const isProgramArchive: boolean = false
  const [selectedProgramName, setSelectedProgramName] = React.useState<string>('')
  const [selectedModelYear, setSelectedModelYear] = React.useState<string>('')
  const [customProgramName, setCustomProgramName] = React.useState<string>('')
  const [description, setDescription] = React.useState<string>('')
  const gmid = useAppSelector(state => selectCurrentUser(state).id)
  const userFullName = useAppSelector((state) => {
    const firstName = selectAccessControlState(state)?.currentUser?.firstName
    const lastName = selectAccessControlState(state)?.currentUser?.lastName
    return `${firstName} ${lastName}`
  })
  const user = useAppSelector(selectCurrentUser)
  const hasWriteAccess: boolean = useHasWriteAccess()
  const isAdmin = getIsUserAdmin(user)
  const saveHandle = usePostRequest('/HttpTriggerAdasPrograms?page=landingTable&requestType=addProgram')

  const currentProgramName = useMemo(() => checkedAddToExistingGroup ? selectedProgramName : customProgramName, [checkedAddToExistingGroup, selectedProgramName, customProgramName])

  const [modelYears, setModelYears] = useState<string[]>(generateModelYearRange(true) as string[])
  useEffect(() => {
    const allModelYears = generateModelYearRange(true) as string[];
    const excludedYears = new Set(props.programNametoModelYearsMap[currentProgramName]);
    const filteredModelYears = allModelYears.filter(year => !excludedYears.has(year));
    setModelYears(filteredModelYears);
  }, [currentProgramName, props.programNametoModelYearsMap])

  const onSave = () => {
    const programName = checkedAddToExistingGroup ? selectedProgramName : customProgramName
    const modelYear = selectedModelYear
    const createdBy = userFullName
    const _isPlanOfRecord: boolean = isPlanOfRecord
    const _isProgramArchive: boolean = isProgramArchive
    const payload = {
      programName,
      modelYear,
      description,
      createdBy,
      gmid,
      _isPlanOfRecord,
      _isProgramArchive
    }

    saveHandle(payload)
      .then((res) => {
        if (res.error) {
          toast.error(res.error, {duration: VEHICLE_PROGRAMS_CONSTANTS.toast.TOAST_ERROR_DURATION})
        } else {
          props.onCancel(true)
        }
      })
      .catch((err) => {
        console.error(err)
        toast.error('Error saving program')
      })
  }

  const checkDisabled = () => {
    let progName = checkedAddToExistingGroup ? selectedProgramName : customProgramName
    return props.programNametoModelYearsMap?.[progName]?.has(selectedModelYear) ?? false;
  }
  const isDuplicateModelYear = checkDisabled()

  const isSaveDisabled = selectedModelYear === ''

  const onChange = (e: any) => {
    const newValue = e.target.value;

    if (!newValue.match(VAR_COMMON_CONSTANTS.VAR_FORBIDDEN_CHARS)) {
      setError("");
    } else {
      setError(VAR_COMMON_CONSTANTS.VAR_FORBIDDEN_CHARS_MSG);
    }
    setCustomProgramName(newValue)
  };
  return (
    <ArgosFlexbox vertical>
      <FormControlLabel
        control={
          <Checkbox
            checked={checkedAddToExistingGroup}
            onChange={(e) => setCheckedAddToExistingGroup(e.target.checked)}
          />
        }
        label={'Add to Existing Program Group'}
      />
      <ArgosSelect
        disabled={!checkedAddToExistingGroup}
        value={selectedProgramName}
        label="Select ADAS Program"
        onChange={(e: any) => {
          setSelectedProgramName(e.target.value)
        }}
      >
        {
          props?.programsList.map((program: any, i: number) => {
            return (
              <ArgosMenuItem key={i} value={program.programName}>{program.programName}</ArgosMenuItem>
            )
          })
        }
      </ArgosSelect>
      <ArgosTextField
        disabled={checkedAddToExistingGroup}
        label={'Program Name'} required value={customProgramName}
        onChange={onChange}
        error={!!error}
        helperText={error}
      />

      <FormControl error={isDuplicateModelYear}>
        <ArgosSelect
          // disabled
          error={isDuplicateModelYear}
          value={selectedModelYear}
          label="Select Model Year"
          onChange={(e: any) => {
            setSelectedModelYear(e.target.value)
          }}
        >
          {
            modelYears.map((modelYear, i) => {
              return (
                <ArgosMenuItem key={i} value={modelYear}>{modelYear}</ArgosMenuItem>
              )
            })
          }
        </ArgosSelect>
        <FormHelperText
          hidden={!isDuplicateModelYear}
        >
          {'duplicate model year'}
        </FormHelperText>
      </FormControl>

      <ArgosTextField
        rows={4}
        multiline label={"Notes/Description"} value={description} onChange={(e) => setDescription(e.target.value)}/>

      <ArgosFlexbox style={{justifyContent: 'center'}}>
        <ArgosButton onClick={() => props?.onCancel()}>Cancel</ArgosButton>
        <ArgosButton disabled={isSaveDisabled || !!error || isDuplicateModelYear} onClick={onSave}>Save</ArgosButton>
      </ArgosFlexbox>
    </ArgosFlexbox>
  )
}

interface IRowActionPermissions {
  editAdasProgram: boolean
  deleteAdasProgram: boolean
}
/**
 * This function generates the row actions for the Adas Programs table.
 * It includes actions for viewing details, editing, and deleting a program.
 * @param row 
 * @param permissions 
 * @param paramSetState 
 * @param dispatch 
 * @param deleteProgram 
 * @returns JSX.Element
 */
const tableRowActions = (row: any, permissions: IRowActionPermissions, paramSetState: any,
                         dispatch: any, deleteProgram: any) => {
  // item details
  const adasProgramId = row?.original?.meta?.adasProgramId
  const programName = row?.original?.programName
  const modelYear = row?.original?.modelYear
  const url = `${ADAS_PROGRAMS_CONSTANTS.itemDetailsRoute}?adasProgramId=${adasProgramId}`

  return (
    <Box
      sx={{
        display: row.subRows?.length ? 'none' : 'flex',
        flexWrap: 'nowrap'
      }}
    >
      <Tooltip title={"View program details"} arrow>
        <a
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            const component = {
              'params': {adasProgramId},
              'name': 'AdasProgramsItemDetails'
            }
            dispatch(setGlobalDialogComponent(component))
            dispatch(setIsGlobalDialogOpen(true))
          }}
          onContextMenu={(e) => {
            e.preventDefault()
            dispatch(setGlobalRightClickMenuData({url}))
            dispatch(setGlobalRightClickMenuPosition({x: e.pageX, y: e.pageY}))
          }}
          href={url}
          target={'_blank'}
        >
          <IconButton size="large">
            <PageviewOutlined fontSize="medium" style={{color: tableButtonGray}}/>
          </IconButton>
        </a>
      </Tooltip>
      {permissions.editAdasProgram && <Tooltip title={"Edit Program"} arrow>
        <a
          onClick={(e) => {
            paramSetState()
          }}
        >
          <IconButton size="large">
            <EditIcon fontSize="medium" style={{color: tableButtonGray}}/>
          </IconButton>
        </a>
      </Tooltip>}
      {permissions.deleteAdasProgram && <Tooltip title={"Delete Program"} arrow>
        <a
          onClick={(e) => {
            deleteProgram({id: adasProgramId, name: programName, modelYear})
          }}
        >
          <IconButton size="large">
            <DeleteIcon fontSize="medium" style={{color: tableButtonGray}}/>
          </IconButton>
        </a>
      </Tooltip>}
    </Box>
  )
}

export default AdasPrograms;